/**
 * Permission-based route access.
 *
 * Roles are now configurable permission sets. This file gates navigation by
 * capability codes instead of hard-coded role names.
 */
import type { CurrentUser, EfsPermission } from './permissions';
import { hasAnyPermission, hasPermission } from './permissions';

interface RouteAccess {
  prefix: string;
  permissions: EfsPermission[];
}

interface BatchRouteAccess {
  suffix: string;
  permissions: EfsPermission[];
}

const OUTBOUND_PERMISSIONS: EfsPermission[] = [
  'sales.read',
  'shipment.manage',
  'secondary_process.manage',
  'processing.material_reentry.confirm',
  'mass_balance.manage',
  'sales.actual_result.confirm',
  'sales.lock',
];

const BATCH_ROUTE_ACCESS: BatchRouteAccess[] = [
  { suffix: '/imports', permissions: ['import.manage'] },
  { suffix: '/inbound', permissions: ['receiving.read', 'inbound.manage'] },
  { suffix: '/sorting', permissions: ['sorting.manage'] },
  { suffix: '/dismantling', permissions: ['dismantling.manage'] },
  {
    suffix: '/secondary-process',
    permissions: OUTBOUND_PERMISSIONS,
  },
  { suffix: '/inventory', permissions: ['inventory.manage'] },
  {
    suffix: '/shipments',
    permissions: OUTBOUND_PERMISSIONS,
  },
  { suffix: '/evidence', permissions: ['evidence.read', 'evidence.manage'] },
  { suffix: '/mass-balance', permissions: ['mass_balance.read', 'mass_balance.manage'] },
  { suffix: '/audit-package', permissions: ['audit_package.manage'] },
  { suffix: '/exceptions', permissions: ['exception.manage', 'exception.report.create'] },
  { suffix: '/approvals', permissions: ['approval.read'] },
  {
    suffix: '/weight-difference',
    permissions: ['receiving.read', 'receiving.individual_weight.input'],
  },
  {
    suffix: '/disassembly-batches',
    permissions: ['processing.read', 'processing.dismantling.request'],
  },
  {
    suffix: '/sales-outbounds',
    permissions: OUTBOUND_PERMISSIONS,
  },
  {
    suffix: '/outbounds',
    permissions: OUTBOUND_PERMISSIONS,
  },
  { suffix: '/first-valuation', permissions: ['estimation.read'] },
  {
    suffix: '/valuation/monthly-material',
    permissions: ['valuation.read', 'valuation.market_price.apply'],
  },
  {
    suffix: '/valuation/getit-purchase-price',
    permissions: ['valuation.read', 'valuation.market_price.apply'],
  },
  {
    suffix: '/valuation/supplier-purchase-price',
    permissions: ['valuation.read', 'valuation.market_price.apply'],
  },
  {
    suffix: '/settlement',
    permissions: ['valuation.read', 'settlement.read', 'settlement.manage'],
  },
  {
    suffix: '/reports',
    permissions: [
      'report.external_monthly.view',
      'report.customer_settlement.view',
      'report.processor_invoice.view',
      'report.internal_analysis.view',
      'report.manage',
    ],
  },
  {
    suffix: '/supplier-quotation',
    permissions: ['quotation.supplier.manage', 'quotation.supplier.publish'],
  },
  {
    suffix: '/buyer-proposals',
    permissions: [
      'sales.buyer_proposal.read',
      'sales.buyer_proposal.create',
      'sales.buyer_proposal.decide',
    ],
  },
  { suffix: '/dispatch', permissions: ['dispatch.request.read'] },
];

export const ROUTE_ACCESS: RouteAccess[] = [
  // FR-016 blocker dashboard — mirror the backend gate so the nav link is only
  // shown to users the /api/dashboard/blockers endpoint would accept.
  { prefix: '/blockers', permissions: ['dashboard.audit.read'] },
  { prefix: '/dispatch', permissions: ['dispatch.request.read'] },
  // Supplier portal (P0-N5): any of the supplier-facing capabilities opens
  // the own-cases pages; the backend additionally party-scopes every read.
  {
    prefix: '/supplier/batches',
    permissions: [
      'quotation.supplier.view',
      'exception.supplier.reply',
      'report.external_monthly.view',
      'batch.read',
    ],
  },
  { prefix: '/supplier', permissions: ['dispatch.request.read'] },
  {
    prefix: '/platform',
    permissions: ['dispatch.request.route', 'dispatch.request.reject_platform'],
  },

  { prefix: '/settings/users', permissions: ['user.manage'] },
  { prefix: '/settings/roles', permissions: ['role.read'] },
  { prefix: '/settings/work-packages', permissions: ['workpackage.read'] },
  { prefix: '/settings/companies', permissions: ['company.read'] },
  { prefix: '/settings/facilities', permissions: ['facility.read'] },

  { prefix: '/approvals', permissions: ['approval.read'] },
  { prefix: '/audit-package', permissions: ['audit_package.manage'] },
  { prefix: '/mass-balance', permissions: ['mass_balance.read'] },
  { prefix: '/exceptions', permissions: ['exception.manage', 'exception.report.create'] },
  { prefix: '/evidence/library', permissions: ['file_library.read', 'file_library.manage'] },
  { prefix: '/imports', permissions: ['import.manage'] },
  {
    prefix: '/pricing',
    permissions: ['pricing.manage', 'market_reference.input', 'market_price.confirm'],
  },
  {
    prefix: '/valuation/supplier-quotation',
    permissions: ['quotation.supplier.manage', 'quotation.supplier.publish'],
  },
  {
    prefix: '/operations/buyer-proposals',
    permissions: [
      'sales.buyer_proposal.read',
      'sales.buyer_proposal.create',
      'sales.buyer_proposal.decide',
    ],
  },
  { prefix: '/operations/secondary-process', permissions: OUTBOUND_PERMISSIONS },
  { prefix: '/operations/shipments', permissions: OUTBOUND_PERMISSIONS },
  { prefix: '/valuation', permissions: ['estimation.read', 'valuation.read'] },
  { prefix: '/settlement', permissions: ['settlement.read', 'settlement.manage'] },
  {
    prefix: '/reports',
    permissions: [
      'report.external_monthly.view',
      'report.customer_settlement.view',
      'report.processor_invoice.view',
      'report.internal_analysis.view',
      'report.manage',
    ],
  },
  { prefix: '/masters', permissions: ['master.read'] },
  {
    prefix: '/operations',
    permissions: [
      'inbound.manage',
      'sorting.manage',
      'dismantling.manage',
      'secondary_process.manage',
      'inventory.manage',
      'shipment.manage',
      'dispatch.request.read',
    ],
  },
  { prefix: '/evidence', permissions: ['evidence.manage'] },
  {
    prefix: '/operations/weight-difference',
    permissions: ['receiving.read', 'receiving.individual_weight.input'],
  },
  {
    prefix: '/operations/disassembly-batches',
    permissions: ['processing.read', 'processing.dismantling.request'],
  },
  {
    prefix: '/operations/sales-outbounds',
    permissions: OUTBOUND_PERMISSIONS,
  },
  {
    prefix: '/operations/outbounds',
    permissions: OUTBOUND_PERMISSIONS,
  },
  {
    prefix: '/disassembly-batches',
    permissions: ['processing.read', 'processing.material_reentry.confirm'],
  },
  { prefix: '/corrections', permissions: ['correction.request', 'correction.approve'] },
  { prefix: '/batches', permissions: ['batch.read'] },
  { prefix: '/', permissions: ['dashboard.read'] },
];

function permissionsForBatchPath(pathname: string): EfsPermission[] | null {
  const match = pathname.match(/^\/batches\/[^/]+(\/.*)?$/);
  if (!match) return null;

  const suffix = match[1] ?? '';
  if (suffix === '') return ['batch.read'];

  const sorted = [...BATCH_ROUTE_ACCESS].sort((a, b) => b.suffix.length - a.suffix.length);
  for (const route of sorted) {
    if (suffix === route.suffix || suffix.startsWith(`${route.suffix}/`)) {
      return route.permissions;
    }
  }

  return ['batch.read'];
}

const pathnameWithoutQueryOrHash = (pathname: string): string => {
  const queryIndex = pathname.indexOf('?');
  const hashIndex = pathname.indexOf('#');
  const cutPoints = [queryIndex, hashIndex].filter((index) => index >= 0);
  const cutAt = cutPoints.length > 0 ? Math.min(...cutPoints) : pathname.length;
  return pathname.slice(0, cutAt) || '/';
};

export function permissionsForPath(pathname: string): EfsPermission[] | null {
  const accessPath = pathnameWithoutQueryOrHash(pathname);
  const batchPermissions = permissionsForBatchPath(accessPath);
  if (batchPermissions) return batchPermissions;

  const sorted = [...ROUTE_ACCESS].sort((a, b) => b.prefix.length - a.prefix.length);
  for (const route of sorted) {
    if (route.prefix === '/' ? accessPath === '/' : accessPath.startsWith(route.prefix)) {
      return route.permissions;
    }
  }
  return null;
}

export function canAccessPath(user: CurrentUser | null, pathname: string): boolean {
  if (hasPermission(user, 'system.admin')) return true;
  const permissions = permissionsForPath(pathname);
  if (!permissions) return true;
  return hasAnyPermission(user, permissions);
}

export function homeRouteFor(user: CurrentUser | null): string {
  if (!user) return '/login';
  if (hasPermission(user, 'dashboard.read')) return '/';
  if (hasPermission(user, 'dispatch.request.read')) return '/dispatch';
  if (hasPermission(user, 'master.read')) return '/masters/incoming-items';
  if (hasPermission(user, 'role.read')) return '/settings/roles';
  return '/forbidden';
}
